import React, { useState, useEffect, useRef } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './AITestBot.css';

const AITestBot = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [isCallActive, setIsCallActive] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [testResults, setTestResults] = useState([]);
  const [currentTest, setCurrentTest] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [apiStatus, setApiStatus] = useState('disconnected');
  const callTimerRef = useRef(null);

  // Test scenarios for Slovak taxi dispatcher
  const testScenarios = [
    {
      id: 'booking',
      title: 'Objednávka taxíka',
      description: 'Test základnej objednávky taxíka',
      prompt: 'Dobrý deň, potrebujem si objednať taxík na Hlavnú ulicu 15, Bratislava.',
      expectedKeywords: ['adresa', 'čas', 'kontakt', 'potvrdenie']
    },
    {
      id: 'urgent',
      title: 'Urgentná jazda',
      description: 'Test urgentnej objednávky',
      prompt: 'Naliehavé! Potrebujem taxík na nemocnicu, čo najrýchlejšie!',
      expectedKeywords: ['priorita', 'nemocnica', 'rýchlo', 'dostupnosť']
    },
    {
      id: 'pricing',
      title: 'Cenové informácie',
      description: 'Test otázok o cenách',
      prompt: 'Koľko by stála jazda z centra na letisko?',
      expectedKeywords: ['cena', 'vzdialenosť', 'tarifa', 'odhad']
    },
    {
      id: 'availability',
      title: 'Dostupnosť vozidiel',
      description: 'Test kontroly dostupnosti',
      prompt: 'Máte voľné taxíky v okolí Petržalky?',
      expectedKeywords: ['dostupnosť', 'lokácia', 'čakanie', 'najbližší']
    }
  ];

  useEffect(() => {
    // Check API connection and load existing orders
    checkApiConnection();
    loadTaxiOrders();
    
    // Refresh orders every 10 seconds
    const interval = setInterval(loadTaxiOrders, 10000);
    
    return () => {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
      clearInterval(interval);
    };
  }, []);

  const checkApiConnection = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('http://localhost:5000/api/health');
      const data = await response.json();
      
      if (data.status === 'healthy') {
        setApiStatus('connected');
        setIsConnected(true);
      } else {
        setApiStatus('error');
      }
    } catch (error) {
      setApiStatus('error');
      console.error('API Connection failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTaxiOrders = async () => {
    try {
      const response = await fetch('http://localhost:5000/api/taxi-orders');
      const data = await response.json();
      
      if (data.orders) {
        // Convert taxi orders to test results format
        const orderResults = data.orders.map(order => ({
          id: order.id,
          type: 'call',
          phoneNumber: order.phone_number,
          pickup: order.pickup_location,
          destination: order.destination,
          fullSpeech: order.full_speech,
          timestamp: new Date(order.created_at).toLocaleString('sk-SK'),
          status: order.status,
          callSid: order.call_sid
        }));
        
        // Merge with existing test results, avoid duplicates
        setTestResults(prev => {
          const existingIds = prev.map(r => r.callSid).filter(Boolean);
          const newOrders = orderResults.filter(order => !existingIds.includes(order.callSid));
          return [...newOrders, ...prev];
        });
      }
    } catch (error) {
      console.error('Error loading taxi orders:', error);
    }
  };

  const startCall = async () => {
    if (!phoneNumber.trim()) {
      alert('Prosím zadajte telefónne číslo');
      return;
    }

    setIsCallActive(true);
    setCallDuration(0);
    
    // Start call timer
    callTimerRef.current = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);

    try {
      const response = await fetch('http://localhost:5000/api/initiate-call', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone_number: phoneNumber
        })
      });
      
      const data = await response.json();
      console.log('Call initiated:', data);
    } catch (error) {
      console.error('Error initiating call:', error);
    }
  };

  const endCall = () => {
    setIsCallActive(false);
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
    }
    
    // Save call log
    const callLog = {
      id: Date.now(),
      duration: callDuration,
      timestamp: new Date().toLocaleString('sk-SK'),
      phoneNumber: phoneNumber,
      status: 'completed'
    };
    
    setTestResults(prev => [callLog, ...prev]);
  };

  const testAIResponse = async (scenario) => {
    setCurrentTest(scenario.title);
    setIsLoading(true);
    setAiResponse('');

    try {
      const response = await fetch('http://localhost:5000/api/test-ai', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: scenario.prompt
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setAiResponse(data.ai_response);
        
        // Analyze response quality
        const keywordMatches = scenario.expectedKeywords.filter(keyword => 
          data.ai_response.toLowerCase().includes(keyword.toLowerCase())
        );
        
        const testResult = {
          id: Date.now(),
          scenario: scenario.title,
          prompt: scenario.prompt,
          response: data.ai_response,
          keywordMatches: keywordMatches.length,
          totalKeywords: scenario.expectedKeywords.length,
          score: Math.round((keywordMatches.length / scenario.expectedKeywords.length) * 100),
          timestamp: new Date().toLocaleString('sk-SK'),
          model: data.model_used,
          confidence: data.confidence
        };
        
        setTestResults(prev => [testResult, ...prev]);
      } else {
        setAiResponse('Chyba pri komunikácii s AI modelom.');
      }
      
    } catch (error) {
      console.error('AI Test failed:', error);
      setAiResponse('Chyba pri testovaní AI asistenta.');
    } finally {
      setIsLoading(false);
      setCurrentTest('');
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="ai-test-page">
      <Header />
      
      <main className="ai-test-main">
        <div className="ai-test-container">
          {/* Header Section */}
          <div className="test-header">
            <h1>AI Dispečing - Testovanie</h1>
            <p>Rozhranie pre testovanie slovenského AI asistenta pre taxi spoločnosti</p>
            
            <div className="api-status">
              <div className={`status-indicator ${apiStatus}`}>
                <div className="status-dot"></div>
                <span>
                  {apiStatus === 'connected' && 'Pripojené'}
                  {apiStatus === 'disconnected' && 'Odpojené'}
                  {apiStatus === 'error' && 'Chyba pripojenia'}
                </span>
              </div>
            </div>
          </div>

          {/* Phone Testing Section */}
          <div className="test-section">
            <h2>🔊 Telefonické testovanie</h2>
            <div className="phone-test">
              <div className="phone-input">
                <input
                  type="tel"
                  placeholder="+421 XXX XXX XXX"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  disabled={isCallActive}
                />
                {!isCallActive ? (
                  <button 
                    className="call-btn start-call"
                    onClick={startCall}
                    disabled={!isConnected}
                  >
                    📞 Spustiť hovor
                  </button>
                ) : (
                  <button 
                    className="call-btn end-call"
                    onClick={endCall}
                  >
                    📞 Ukončiť hovor
                  </button>
                )}
              </div>
              
              {isCallActive && (
                <div className="call-status">
                  <div className="call-indicator">
                    <div className="pulse"></div>
                    <span>Aktívny hovor - {formatDuration(callDuration)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* AI Response Testing */}
          <div className="test-section">
            <h2>🤖 Testovanie AI odpovedí</h2>
            <div className="scenario-tests">
              {testScenarios.map(scenario => (
                <div key={scenario.id} className="scenario-card">
                  <h3>{scenario.title}</h3>
                  <p>{scenario.description}</p>
                  <div className="scenario-prompt">
                    <strong>Test prompt:</strong> "{scenario.prompt}"
                  </div>
                  <button
                    className="test-btn"
                    onClick={() => testAIResponse(scenario)}
                    disabled={isLoading || currentTest === scenario.title}
                  >
                    {currentTest === scenario.title ? 'Testuje sa...' : 'Testovať'}
                  </button>
                </div>
              ))}
            </div>
            
            {aiResponse && (
              <div className="ai-response">
                <h3>AI Odpoveď:</h3>
                <div className="response-text">"{aiResponse}"</div>
              </div>
            )}
          </div>

          {/* Test Results */}
          <div className="test-section">
            <h2>📊 Výsledky testov</h2>
            <div className="results-container">
              {testResults.length === 0 ? (
                <p className="no-results">Zatiaľ žiadne výsledky testov</p>
              ) : (
                <div className="results-list">
                  {testResults.map(result => (
                    <div key={result.id} className="result-card">
                      <div className="result-header">
                        <h4>
                          {result.type === 'call' ? '📞 Skutočný hovor' : 
                           result.scenario ? `🤖 ${result.scenario}` : '📞 Test hovor'}
                        </h4>
                        <span className="timestamp">{result.timestamp}</span>
                      </div>
                      
                      {result.type === 'call' ? (
                        // Real Phone Call from Twilio
                        <div className="result-content">
                          <div className="call-info">
                            <strong>📱 Číslo zákazníka:</strong> {result.phoneNumber}
                          </div>
                          <div className="pickup">
                            <strong>📍 Vyzdvihnutie:</strong> {result.pickup || 'Nespecifikované'}
                          </div>
                          <div className="destination">
                            <strong>🎯 Cieľ:</strong> {result.destination || 'Nespecifikované'}
                          </div>
                          <div className="speech">
                            <strong>🎤 Celý text:</strong> "{result.fullSpeech}"
                          </div>
                          <div className="status">
                            <strong>Status:</strong> 
                            <span className={`status-badge ${result.status}`}>
                              {result.status === 'received' ? '✅ Prijatá' : result.status}
                            </span>
                          </div>
                        </div>
                      ) : result.scenario ? (
                        // AI Response Test Result
                        <div className="result-content">
                          <div className="prompt">
                            <strong>Prompt:</strong> {result.prompt}
                          </div>
                          <div className="response">
                            <strong>Odpoveď:</strong> {result.response}
                          </div>
                          <div className="score">
                            <strong>Skóre:</strong> {result.score}% 
                            ({result.keywordMatches}/{result.totalKeywords} kľúčových slov)
                          </div>
                          {result.model && (
                            <div className="model">
                              <strong>Model:</strong> {result.model}
                            </div>
                          )}
                        </div>
                      ) : (
                        // Legacy Phone Call Result
                        <div className="result-content">
                          <div className="call-info">
                            <strong>Číslo:</strong> {result.phoneNumber}
                          </div>
                          <div className="duration">
                            <strong>Trvanie:</strong> {formatDuration(result.duration)}
                          </div>
                          <div className="status">
                            <strong>Stav:</strong> {result.status}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* API Integration Info */}
          <div className="test-section">
            <h2>⚙️ API Integrácia</h2>
            <div className="api-info">
              <div className="api-card">
                <h3>Twilio Voice API</h3>
                <p>Status: {isConnected ? '✅ Pripojené' : '❌ Odpojené'}</p>
                <p>Endpoint: Voice calls & SMS</p>
              </div>
              <div className="api-card">
                <h3>SlovakBERT Model</h3>
                <p>Status: {isConnected ? '✅ Pripojené' : '❌ Odpojené'}</p>
                <p>Model: gerulata/slovakbert</p>
              </div>
              <div className="api-card">
                <h3>HuggingFace Inference</h3>
                <p>Status: {isConnected ? '✅ Pripojené' : '❌ Odpojené'}</p>
                <p>Provider: hf-inference</p>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default AITestBot; 